import { useMemo, useState } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { Dashboard } from './components/dashboard/Dashboard';
import { DashboardLayout } from './components/dashboard/DashboardLayout';
import { useJsonDebugger } from './hooks/useJsonDebugger';
import { useDashboardData } from './hooks/useDashboardData';
import { Overview } from './components/dashboard/Overview';
import { IncidentsSection } from './components/dashboard/sections/IncidentsSection';
import { CatalogTasksSection } from './components/dashboard/sections/CatalogTasksSection';
import { DashboardStats } from './components/dashboard/DashboardStats';
import { useLinkedIncidents } from './hooks/useLinkedIncidents';
import { useRepetitiveTickets } from './hooks/useRepetitiveTickets';
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { AlertTriangle } from "lucide-react";
import { PostDeploymentIncidents } from './components/dashboard/PostDeploymentIncidents';
import { RepetitiveTickets } from './components/dashboard/RepetitiveTickets';
import { TabsContent } from "@/components/ui/tabs";
import { SystemReliabilityScore } from './components/dashboard/SystemReliabilityScore';
import { DeploymentRiskAssessment } from './components/dashboard/DeploymentRiskAssessment';
import { ExecutiveSummary } from './components/dashboard/ExecutiveSummary';
import { AuthGuard } from './components/AuthGuard';
import Login from './pages/Login';
import Index from './pages/Index';
import './App.css';
import { AIAnalysis } from './components/AIAnalysis';

function App() {
  // Use JSON debugger to load and debug JSON data
  const jsonDebugger = useJsonDebugger();
  const [activeTab, setActiveTab] = useState<string>("overview");
  
  // Use dashboard data hook with JSON debugger
  const dashboardData = useDashboardData(undefined, jsonDebugger);
  
  // Make sure we have valid data structures even if loading fails
  const safeFilteredData = useMemo(() => {
    return {
      'Incidents': Array.isArray(dashboardData.filteredData['Incidents']) 
        ? dashboardData.filteredData['Incidents'] 
        : [],
      'Catalog Tasks': Array.isArray(dashboardData.filteredData['Catalog Tasks']) 
        ? dashboardData.filteredData['Catalog Tasks'] 
        : []
    };
  }, [dashboardData.filteredData]);
  
  // Calculate stats from filtered data
  const linkedIncidents = useLinkedIncidents(safeFilteredData);
  
  const repetitiveTicketsData = useRepetitiveTickets(
    safeFilteredData['Incidents'],
    safeFilteredData['Catalog Tasks']
  );
  
  const stats = useMemo(() => {
    const incidents = safeFilteredData['Incidents'] || [];
    const catalogTasks = safeFilteredData['Catalog Tasks'] || [];
    
    const highPriorityIncidents = incidents.filter(incident => {
      if (!incident) return false;
      const priority = (incident.priority || "").toLowerCase();
      return priority === "high" || priority === "critical" || priority === "1" || priority === "2";
    }).length;

    // Calculate ticket status counts
    const openTickets = incidents.filter(inc => !inc.State || inc.State.toLowerCase().includes('open') || inc.State.toLowerCase().includes('new')).length;
    const inProgressTickets = incidents.filter(inc => inc.State && (inc.State.toLowerCase().includes('progress') || inc.State.toLowerCase().includes('assigned'))).length;
    const closedTickets = incidents.filter(inc => inc.State && inc.State.toLowerCase().includes('closed')).length;
    const resolvedTickets = incidents.filter(inc => inc.State && inc.State.toLowerCase().includes('resolved')).length;
    
    return {
      totalIncidents: incidents.length,
      totalCatalogTasks: catalogTasks.length,
      linkedIncidents: linkedIncidents.length,
      highPriorityIncidents: highPriorityIncidents,
      repetitiveTickets: repetitiveTicketsData.length,
      openTickets,
      inProgressTickets,
      closedTickets,
      resolvedTickets
    };
  }, [safeFilteredData, linkedIncidents, repetitiveTicketsData]);

  const usingMockData = dashboardData.dataSource === 'static';
  
  const handleTabChange = (tab: string) => {
    setActiveTab(tab);
  };
  
  // Parse time period safely, handling "all" and other non-numeric values
  const getTimePeriodDays = (timeRange: string): number => {
    if (!timeRange || timeRange === "all") return 30; // Default to 30 days
    const parsed = parseInt(timeRange);
    return isNaN(parsed) ? 30 : parsed; // Fallback to 30 if parsing fails
  };

  // Debug the timeRange value
  const timePeriodDays = getTimePeriodDays(dashboardData.filters.timeRange);
  console.log('App.tsx - Current timeRange:', dashboardData.filters.timeRange, 'Parsed to days:', timePeriodDays);

  // Protected dashboard content
  const DashboardContent = () => (
    <AuthGuard>
      <DashboardLayout 
        isLoading={dashboardData.isLoading}
        filters={dashboardData.filters}
        apiNames={dashboardData.apiNames}
        assignmentGroups={dashboardData.assignmentGroups}
        activeTab={activeTab}
        onTabChange={handleTabChange}
      >
        {jsonDebugger.error && (
          <Alert variant="destructive" className="mb-4">
            <AlertTriangle className="h-4 w-4" />
            <AlertTitle>Error Loading Data</AlertTitle>
            <AlertDescription>
              {jsonDebugger.error}
            </AlertDescription>
          </Alert>
        )}
        
        {usingMockData && (
          <Alert className="mb-4">
            <AlertTriangle className="h-4 w-4" />
            <AlertTitle>Using Mock Data</AlertTitle>
            <AlertDescription>
              The system is currently using mock data because the JSON files could not be loaded or parsed. Please ensure your JSON files are properly formatted and located in the correct directory.
            </AlertDescription>
          </Alert>
        )}
        
        <TabsContent value="overview">
          <Overview 
            filteredData={safeFilteredData}
            excelData={dashboardData.excelData}
            stats={stats}
            dataSource={dashboardData.dataSource}
            dateRange={dashboardData.filters.dateRange}
          />
        </TabsContent>
        
        <TabsContent value="incidents">
          <IncidentsSection incidents={safeFilteredData['Incidents'] || []} />
        </TabsContent>
        
        <TabsContent value="catalog-tasks">
          <CatalogTasksSection
            incidents={safeFilteredData['Incidents'] || []}
            catalogTasks={safeFilteredData['Catalog Tasks'] || []}
          />
        </TabsContent>
        
        <TabsContent value="post-deployment">
          <h2 className="text-2xl font-bold mb-6">Post-Deployment Incident Detection</h2>
          <PostDeploymentIncidents 
            linkedIncidents={linkedIncidents} 
            showFullAnalytics 
            catalogTasks={safeFilteredData['Catalog Tasks'] || []}
            incidents={safeFilteredData['Incidents'] || []}
          />
        </TabsContent>
        
        <TabsContent value="repetitive">
          <h2 className="text-2xl font-bold mb-6">Repetitive Ticket Insights</h2>
          <RepetitiveTickets
            incidents={safeFilteredData['Incidents'] || []}
            catalogTasks={safeFilteredData['Catalog Tasks'] || []}
            showFullAnalysis
          />
        </TabsContent>
        
        <TabsContent value="reliability">
          <h2 className="text-2xl font-bold mb-6">System Reliability & Risk</h2>
          <div className="space-y-6">
            <SystemReliabilityScore
              incidents={safeFilteredData['Incidents'] || []}
              catalogTasks={safeFilteredData['Catalog Tasks'] || []}
            />
            <DeploymentRiskAssessment
              incidents={safeFilteredData['Incidents'] || []}
              catalogTasks={safeFilteredData['Catalog Tasks'] || []}
            />
          </div>
        </TabsContent>
        
        <TabsContent value="executive">
          <h2 className="text-2xl font-bold mb-6">Executive Summary</h2>
          <ExecutiveSummary
            incidents={safeFilteredData['Incidents'] || []}
            catalogTasks={safeFilteredData['Catalog Tasks'] || []}
          />
        </TabsContent>
        
        <TabsContent value="ai-analysis">
          <h2 className="text-2xl font-bold mb-6">AI-Powered Analysis</h2>
          <p className="text-muted-foreground mb-6">
            Advanced analysis of incidents and patterns using machine learning.
          </p>
          <AIAnalysis
            incidents={safeFilteredData['Incidents'] || []}
            catalogTasks={safeFilteredData['Catalog Tasks'] || []}
            timePeriodDays={timePeriodDays}
          />
        </TabsContent>
      </DashboardLayout>
    </AuthGuard>
  );
  
  return (
    <BrowserRouter>
      <Routes>
        <Route path="/" element={<Index />} />
        <Route path="/login" element={<Login />} />
        <Route path="/dashboard" element={<DashboardContent />} />
        <Route path="incidents" element={<DashboardContent />} />
        <Route path="catalog-tasks" element={<DashboardContent />} />
      </Routes>
    </BrowserRouter>
  );
}

export default App;
